import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import color from 'color';

/**
 * Theming Service
 */
@Injectable({ providedIn: 'root' })
export class ThemingService {
    // Default color scheme
    private colors = {
        primary: '#26314C',
        secondary: '#F52D55',
        success: '#6CD4A8',
        danger: '#EFC772',
        error: '#F44436',
        info: '#8290FD'
    };
    private palettes = [];

    constructor(@Inject(DOCUMENT) private document: Document) {}

    getColors() {
        return this.colors;
    }

    getPalettes() {
        return this.palettes;
    }

    apply(colors?) {
        this.colors = { ...this.colors, ...colors };
        this.palettes = [];

        // Aplica los colores originales como variables CSS antes de generar las variaciones
        this.setCssVariablesForOriginals(this.colors);

        // Genera paletas de variaciones de luminosidad para todos los colores
        for (const color in this.colors) {
            this.palettes.push(this.generatePalette(color, this.colors[color]));
        }

        // Aplica las paletas generadas como variables CSS
        this.setCssVariables(this.palettes);
    }

    private setCssVariables(palettes) {
        palettes.forEach((palette) => {
            for (const variation in palette.map) {
                this.document.documentElement.style.setProperty(
                    `--v2-color-${palette.name}-${variation}`,
                    palette.map[variation].c
                );
            }
        });
    }

    private setCssVariablesForOriginals(colors) {
        // Establece las variables CSS para los colores originales sin modificaciones
        for (const colorName in colors) {
            this.document.documentElement.style.setProperty(
                `--v2-color-${colorName}-500`,
                colors[colorName]
            );
        }
    }


    generatePalette(colorName, value) {
        const palette = {
            name: colorName,
            map: {}
        };

        // Lightness levels fixed by step — independent of the base color's lightness.
        // This guarantees -100 is always near-white and -900 is always very dark,
        // regardless of whether the base is pink, blue, green, etc.
        const fixedLightness = {
            100: 97,
            200: 90,
            300: 78,
            400: 63,
            500: null, // use base color as-is
            600: null, // derive from base
            700: null,
            800: null,
            900: null,
        };

        let baseHsl;
        try {
            const c = color(value);
            if (c.hex().toUpperCase() === '#FFFFFF') throw new Error('white');
            baseHsl = c.hsl();
        } catch (e) {
            baseHsl = color('#949494').hsl();
        }

        const baseL = baseHsl.color[2];

        // Dark steps: reduce lightness proportionally below the base
        const darkStep = Math.max(3, baseL / 10);
        fixedLightness[600] = Math.max(5, baseL - darkStep);
        fixedLightness[700] = Math.max(5, baseL - darkStep * 2);
        fixedLightness[800] = Math.max(5, baseL - darkStep * 3);
        fixedLightness[900] = Math.max(5, baseL - darkStep * 4);

        const h = baseHsl.hue();
        const s = baseHsl.saturationl();

        for (let i = 1; i <= 9; i++) {
            const level = i * 100;
            const l = fixedLightness[level] !== null ? fixedLightness[level] : baseL;
            palette.map[level] = { c: color.hsl(h, s, l).hex() };
        }

        return palette;
    }


    getVariationFromColor(colorHex: string, variation: number): string {
        let c;
        try {
            c = color(colorHex);
        } catch (e) {
            c = color('#808080'); // fallback - gray hex
        }
        c = c.hsl();
        c.color[2] = Math.max(0, Math.min(100, c.color[2] + (variation - 500) / 10.5)); // Ajusta la luminosidad en pasos de 10%

        return c.hex();
    }
}





